import { portalApi } from '@/lib/http/axios';

/**
 * The purpose tag on a DANI conversation (M4S-5912, docs/pdlc/credits-ui.md §6).
 *
 * A feature that runs its work as a DANI conversation tags it before the first
 * message, so the DANI writer labels the conversation's usage with the feature
 * and links it to the check or survey. The tag is awaited, but never for more
 * than `PURPOSE_TAG_TIMEOUT_MS`, and it never blocks the message: whatever
 * happens, the caller sends. A conversation whose tag failed counts as chat.
 *
 * Never throws. The outcome says what happened so the feature can log it on its
 * own record (Feasibility logs it on the check, `purposeTagLogDetail`).
 */

export const PURPOSE_TAG_TIMEOUT_MS = 3000;

/**
 * The request itself is cancelled a little after the wait ends, so the wait's
 * own timer always answers first and a slow API reads as `timeout`, not as a
 * failure with no status (the interceptor rewrites axios's timeout error).
 */
const REQUEST_GRACE_MS = 500;

export type ConversationPurpose = 'feasibility' | 'survey_build';

export interface ConversationPurposeTag {
    purpose: ConversationPurpose;
    /** The check id or survey id the conversation runs for. */
    refId?: string | null;
}

export type PurposeTagOutcome =
    /** The tag was written. */
    | { status: 'tagged' }
    /** The conversation already had this purpose; the first tag stands. */
    | { status: 'repeat' }
    /** The ledger writer is switched off where the API runs: nothing was written, by design. */
    | { status: 'writer_off' }
    /** The API does not have the tag route (404/501), e.g. a UI deployed ahead of its API. */
    | { status: 'unavailable' }
    /** Refused or failed: 400, 403, 409, 5xx or a network error. */
    | { status: 'failed'; httpStatus: number | null; code: string | null }
    /** No answer within the timeout. */
    | { status: 'timeout' };

interface TagResponse {
    tagged?: boolean;
    writerEnabled?: boolean;
    code?: unknown;
}

/**
 * The PUT, answering every status instead of rejecting. `portalApi`'s response
 * interceptor turns a refusal into a plain Error with no status (a 404 becomes
 * "Resource not found.", a 409 its message) and sends a 403 through the
 * session probe, so the status is read here, with `validateStatus` accepting
 * every answer. Only a network error or the axios timeout rejects.
 */
type Put = (
    url: string,
    body: unknown,
    config: { timeout: number; validateStatus: (status: number) => boolean },
) => Promise<{ status: number; data: TagResponse | null | undefined }>;

const ANY_STATUS = () => true;

export const purposeTagPath = (conversationId: string) =>
    `/credit-consumption/conversations/${encodeURIComponent(conversationId)}/purpose`;

/**
 * What an answer means. A 404 that names no `code` is Nest's own "no such
 * route" (an API deployed ahead of this UI's tag route); the tag service's
 * 404 carries `ORG_NOT_FOUND` and is a failure like any other refusal.
 */
export function purposeTagOutcomeOf(
    status: number,
    data: TagResponse | null | undefined,
): PurposeTagOutcome {
    if (status >= 200 && status <= 299) {
        if (data?.writerEnabled === false) return { status: 'writer_off' };
        return data?.tagged ? { status: 'tagged' } : { status: 'repeat' };
    }
    const code = typeof data?.code === 'string' ? data.code.slice(0, 64) : null;
    if (status === 501 || (status === 404 && code === null)) return { status: 'unavailable' };
    return { status: 'failed', httpStatus: status, code };
}

/** Tags the conversation, waiting at most `timeoutMs`. Resolves, never rejects. */
export async function tagConversationPurpose(
    conversationId: string,
    tag: ConversationPurposeTag,
    options: { timeoutMs?: number; put?: Put } = {},
): Promise<PurposeTagOutcome> {
    const timeoutMs = options.timeoutMs ?? PURPOSE_TAG_TIMEOUT_MS;
    const put: Put =
        options.put ?? ((url, body, config) => portalApi.put<TagResponse>(url, body, config));
    const body = {
        purpose: tag.purpose,
        ...(tag.refId ? { refId: tag.refId } : {}),
    };
    let timer: ReturnType<typeof setTimeout> | undefined;
    const timedOut = new Promise<PurposeTagOutcome>((resolve) => {
        timer = setTimeout(() => resolve({ status: 'timeout' }), timeoutMs);
    });
    const sent = put(purposeTagPath(conversationId), body, {
        timeout: timeoutMs + REQUEST_GRACE_MS,
        validateStatus: ANY_STATUS,
    }).then(
        ({ status, data }) => purposeTagOutcomeOf(status, data),
        (): PurposeTagOutcome => ({ status: 'failed', httpStatus: null, code: null }),
    );
    try {
        return await Promise.race([sent, timedOut]);
    } finally {
        if (timer !== undefined) clearTimeout(timer);
    }
}

/**
 * What a feature logs on its own record, or null when there is nothing to log:
 * with the writer switched off, or the route not deployed, nothing was meant to
 * be written, so nothing is logged and production data stays as it was.
 */
export interface PurposeTagLogDetail {
    status: 'tagged' | 'repeat' | 'failed' | 'timeout';
    httpStatus?: number | null;
    code?: string | null;
}

export function purposeTagLogDetail(outcome: PurposeTagOutcome): PurposeTagLogDetail | null {
    switch (outcome.status) {
        case 'writer_off':
        case 'unavailable':
            return null;
        case 'failed':
            return { status: 'failed', httpStatus: outcome.httpStatus, code: outcome.code };
        default:
            return { status: outcome.status };
    }
}
